import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

// @mui material components
import Checkbox from "@mui/material/Checkbox";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// Images
import curved11 from "assets/images/curved-images/curved11.jpg";

import AuthApi from "../../../api/auth";

const clinicMapping = {
  "WRC": 25,
  "NSR": 26,
  "TLR": 27,
  "THI": 28,
  "SERC": 29,
  "PCD": 30,
  "LH": 31,
  "DIFAD": 36,
  "RRC": 37,
  "EH": 38,
  "IKON": 39,
  "CDR": 41,
  "RRL": 43,
};

function NewUser() {
  const [agreement, setAgreement] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: 'T4k4ham12024!',
    Primary_Key: '',
    role: '',
    first_name: '',
    last_name: '',
    Census_access: false,
    Dashboards_access: false,
    Biller: false,
    census_edit_permission: false
  });
  const [permissionsLocked, setPermissionsLocked] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const handleChange = useCallback(e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  }, [formData]);

  const handleSelectChange = useCallback((name, value) => {
    const updatedForm = {
      ...formData,
      [name]: value
    };

    if (name === 'role') {
      if (value === 'admin') {
        updatedForm.Census_access = true;
        updatedForm.Dashboards_access = true;
        updatedForm.Biller = true;
        updatedForm.census_edit_permission = true;
        setPermissionsLocked(true);
      } else {
        setPermissionsLocked(false);
      }
    }

    setFormData(updatedForm);
  }, [formData]);

  const handleClinicChange = useCallback(selectedClinic => {
    const primaryKey = clinicMapping[selectedClinic] || '';
    handleSelectChange('Primary_Key', primaryKey);
  }, [handleSelectChange]);

  const handleSubmit = useCallback(e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);
    AuthApi.Register(formData).then(response => {
      setLoading(false);
      if (response.data.cognito_id) {
        setSuccess(true);
        setTimeout(() => navigate("/dashboards/work-in-progress"), 2000);
      } else {
        setError(response.data.msg);
      }
    }).catch(error => {
      setLoading(false);
      if (error.response) {
        return setError(error.response.data.msg);
      }
      return setError("There has been an error.");
    });
  }, [formData, navigate]);

  return (
    <CoverLayout
      title="New User Registration"
      description="Please fill the information below to create a new user account"
      image={curved11}
      top={12}
    >
      <SoftBox component="form" role="form">
        {/* Role Select */}
        <SoftBox mb={2}>
          <SoftTypography component="label" variant="caption" fontWeight="bold">Role</SoftTypography>
          <SoftSelect
            title="Role"
            placeholder="Role"
            options={[
              { value: "client", label: "Client" },
              { value: "admin", label: "Admin" }
            ]}
            onChange={e => handleSelectChange('role', e.value)}
          />
        </SoftBox>

        {/* Clinic Select */}
        <SoftBox mb={2}>
          <SoftTypography component="label" variant="caption" fontWeight="bold">Clinic</SoftTypography>
          <SoftSelect
            title="Clinic"
            placeholder="Clinic"
            options={Object.keys(clinicMapping).map(clinic => ({ value: clinic, label: clinic }))}
            onChange={e => handleClinicChange(e.value)}
          />
        </SoftBox>

        {/* First & Last Name */}
        <SoftBox mb={2}>
          <SoftTypography component="label" variant="caption" fontWeight="bold">First Name</SoftTypography>
          <SoftInput placeholder="First Name" type="text" name="first_name" onChange={handleChange} />
        </SoftBox>
        <SoftBox mb={2}>
          <SoftTypography component="label" variant="caption" fontWeight="bold">Last Name</SoftTypography>
          <SoftInput placeholder="Last Name" type="text" name="last_name" onChange={handleChange} />
        </SoftBox>

        {/* Email */}
        <SoftBox mb={2}>
          <SoftTypography component="label" variant="caption" fontWeight="bold">Email</SoftTypography>
          <SoftInput type="email" placeholder="Email" name="email" value={formData.email} onChange={handleChange} />
        </SoftBox>

        {/* Permissions Checkboxes */}
        <SoftBox mb={2}>
          <Checkbox
            checked={formData.Census_access}
            onChange={() => !permissionsLocked && setFormData({ ...formData, Census_access: !formData.Census_access })}
            disabled={permissionsLocked}
          />
          <SoftTypography variant="caption" fontWeight="bold">Census Access</SoftTypography>
        </SoftBox>

        <SoftBox mb={2}>
          <Checkbox
            checked={formData.Dashboards_access}
            onChange={() => !permissionsLocked && setFormData({ ...formData, Dashboards_access: !formData.Dashboards_access })}
            disabled={permissionsLocked}
          />
          <SoftTypography variant="caption" fontWeight="bold">Dashboards Access</SoftTypography>
        </SoftBox>

        <SoftBox mb={2}>
          <Checkbox
            checked={formData.Biller}
            onChange={() => !permissionsLocked && setFormData({ ...formData, Biller: !formData.Biller })}
            disabled={permissionsLocked}
          />
          <SoftTypography variant="caption" fontWeight="bold">Biller</SoftTypography>
        </SoftBox>

        <SoftBox mb={2}>
          <Checkbox
            checked={formData.census_edit_permission}
            onChange={() => !permissionsLocked && setFormData({ ...formData, census_edit_permission: !formData.census_edit_permission })}
            disabled={permissionsLocked}
          />
          <SoftTypography variant="caption" fontWeight="bold">Census Edit Permission</SoftTypography>
        </SoftBox>

        {/* Feedback Messages */}
        <SoftBox mt={2} mb={2} textAlign="center">
          {error && (
            <h6 style={{ fontSize: ".8em", color: "red", fontWeight: 400 }}>{error}</h6>
          )}
          {success && (
            <h6 style={{ fontSize: ".8em", color: "green", fontWeight: 400 }}>
              User created successfully
            </h6>
          )}
        </SoftBox>

        {/* Submit Button */}
        <SoftBox mt={4} mb={1} textAlign="center">
          <SoftButton variant="gradient" color="info" onClick={handleSubmit} fullWidth disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Create User'}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </CoverLayout>
  );
}

export default NewUser;
